import { createContext, ReactNode, useContext, useMemo } from 'react'

export interface BefehlFormConfig {
  /**
   * Controls whether form fields are editable or read-only
   * - true: Fields are not editable
   * - false: Fields are editable
   */
  readOnly: boolean

  /**
   * Controls whether empty required fields show red warning borders
   * - true: Empty fields get red border styling
   * - false: No visual warnings for empty fields
   */
  activateEmptyFieldWarnings: boolean
}

const BefehlFormConfigContext = createContext<BefehlFormConfig | undefined>(
  undefined
)

export interface BefehlFormConfigProviderProps {
  children: ReactNode
  readOnly: boolean
  activateEmptyFieldWarnings: boolean
}

export function BefehlFormConfigProvider({
  children,
  readOnly,
  activateEmptyFieldWarnings
}: Readonly<BefehlFormConfigProviderProps>) {
  const value = useMemo(
    () => ({ readOnly, activateEmptyFieldWarnings }),
    [readOnly, activateEmptyFieldWarnings]
  )

  return (
    <BefehlFormConfigContext.Provider value={value}>
      {children}
    </BefehlFormConfigContext.Provider>
  )
}

export function useBefehlFormConfig() {
  const context = useContext(BefehlFormConfigContext)
  if (!context) {
    throw new Error(
      'useBefehlFormConfig must be used within BefehlFormConfigProvider'
    )
  }
  return context
}

export function useBefehlFormConfigIfAvailable() {
  return useContext(BefehlFormConfigContext)
}
